// Edit planner for view + materialized-view ops (v2 Slice D2).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D2.
//
// All five view ops (create-view, drop-view, create-materialized-view,
// drop-materialized-view, refresh-materialized-view) append a fresh statement
// after the source statement (DSE2-7: no coalescing by default). The source
// statement kind is unrestricted — each op names the target view directly.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeCreateMaterializedView,
  synthesizeCreateView,
  synthesizeDropMaterializedView,
  synthesizeDropView,
  synthesizeRefreshMaterializedView,
} from './dialect.view';

type ViewEdit = Extract<
  DdlEdit,
  {
    op:
      | 'create-view'
      | 'drop-view'
      | 'create-materialized-view'
      | 'drop-materialized-view'
      | 'refresh-materialized-view';
  }
>;

export function planViewEdit(
  ctx: DdlEditCtx,
  edit: ViewEdit,
): readonly PlannedByteEdit[] {
  const synth = synthForView(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: edit.op,
  }];
}

function synthForView(ctx: DdlEditCtx, edit: ViewEdit): string {
  switch (edit.op) {
    case 'create-view':
      return synthesizeCreateView(ctx.engine, edit.view);
    case 'drop-view':
      return synthesizeDropView(
        ctx.engine,
        edit.viewName,
        edit.ifExists ?? false,
        edit.cascade ?? false,
      );
    case 'create-materialized-view':
      return synthesizeCreateMaterializedView(ctx.engine, edit.view);
    case 'drop-materialized-view':
      return synthesizeDropMaterializedView(
        ctx.engine,
        edit.viewName,
        edit.ifExists ?? false,
        edit.cascade ?? false,
      );
    case 'refresh-materialized-view':
      return synthesizeRefreshMaterializedView(
        ctx.engine,
        edit.viewName,
        edit.concurrently ?? false,
        edit.pgsqlWithData,
      );
  }
}
